import React from 'react';
import styled from 'styled-components';
import HowToRead, { SectionShell, EditButton, SubHeading, Panel } from './HowToRead';
import { formatPct, formatMean, summaryHelpParams, questionById } from '../reportModel';

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const KpiCard = styled.div`
  background: ${({ theme }) => theme.colors.report.panel};
  border-radius: 8px;
  padding: 14px 16px;
`;

const KpiLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.report.muted};
  min-height: 30px;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.report.brand};
`;

const KpiSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.report.muted};
`;

const DimGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
`;

const DimCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.report.grayLight};
  border-radius: 8px;
  padding: 14px 16px;
`;

const DimName = styled.div`
  font-weight: 800;
  color: ${({ theme }) => theme.colors.report.brand};
`;

const DimValue = styled.div`
  margin-top: 8px;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.report.blue};
  span { font-size: 13px; font-weight: 400; color: ${({ theme }) => theme.colors.report.muted}; margin-left: 6px; }
`;

const Bar = styled.div`
  height: 6px;
  margin: 10px 0 8px;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.report.grayLight};
  div { height: 100%; border-radius: 3px; background: ${({ theme }) => theme.colors.report.blue}; }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 14px;
  margin-bottom: 8px;
  border-radius: 6px;
  background: ${({ $kind, theme }) => ($kind === 'strength' ? theme.colors.report.strongBg : theme.colors.report.priorityBg)};
  b { font-size: 12px; color: ${({ theme }) => theme.colors.report.brand}; }
  strong { font-size: 16px; color: ${({ $kind, theme }) => ($kind === 'strength' ? theme.colors.report.blue : theme.colors.report.orange)}; }
`;

export default function OrgSummary({ data, narrative, t, lang, canEdit, onEdit }) {
  const invited = data.participation.invited;
  const list = (ids, kind) => ids.map((id) => questionById(data, id)).filter(Boolean).map((q) => (
    <Item key={q.id} $kind={kind}>
      <b>{q.code}</b>
      <span>{q.text}</span>
      <strong>{formatPct(t, q.favorablePct)}</strong>
    </Item>
  ));

  return (
    <SectionShell id="s01" title={t('survey.report.section.s01')}>
      <HowToRead t={t}>{t('survey.report.help.summary', summaryHelpParams(t, data.method))}</HowToRead>

      <KpiGrid>
        <KpiCard>
          <KpiLabel>{invited != null ? t('survey.report.participationRate') : t('survey.report.kpiResponses')}</KpiLabel>
          <KpiValue>{invited != null ? formatPct(t, data.participation.ratePct) : data.participation.responded}</KpiValue>
          {invited != null && (
            <KpiSub>{t('survey.report.kpiParticipationSub', { responded: data.participation.responded, invited })}</KpiSub>
          )}
        </KpiCard>
        {data.headlines.map((h) => (
          <KpiCard key={h.questionId}>
            <KpiLabel>{h.text}{h.code ? ` (${h.code})` : ''}</KpiLabel>
            <KpiValue>{h.suppressed ? t('survey.report.suppressedCell', { n: data.threshold }) : formatPct(t, h.favorablePct)}</KpiValue>
            <KpiSub>{t('survey.report.kpiFavorableSub')}</KpiSub>
          </KpiCard>
        ))}
      </KpiGrid>

      {/* Son inceleme I2 — bölümsüz ankette boş başlık sonuçları çizilmez. */}
      {(data.dimensions || []).length > 0 && (
        <>
          <SubHeading>{t('survey.report.dimensionResults')}</SubHeading>
          <DimGrid>
            {data.dimensions.map((d) => (
              <DimCard key={d.id}>
                <DimName>{d.name}</DimName>
                {d.insufficient ? (
                  <DimValue>{t('survey.report.insufficientCell')}</DimValue>
                ) : (
                  <DimValue>
                    {formatPct(t, d.favorablePct)}
                    <span>{t('survey.report.mean')} {formatMean(d.mean, lang)}</span>
                  </DimValue>
                )}
                <Bar><div style={{ width: `${d.favorablePct || 0}%` }} /></Bar>
                <div>{t('survey.report.dimensionQuestionCount', { count: d.questionCount })}</div>
              </DimCard>
            ))}
          </DimGrid>
        </>
      )}

      <Columns>
        <div>
          <SubHeading>{t('survey.report.strengths')}</SubHeading>
          {list(data.strengths, 'strength')}
        </div>
        <div>
          <SubHeading>{t('survey.report.priorities')}</SubHeading>
          {list(data.priorities, 'priority')}
        </div>
      </Columns>

      {narrative && (
        <Panel>
          <SubHeading style={{ marginTop: 0 }}>
            {t('survey.report.overallAssessment')}
            {canEdit && <EditButton t={t} onClick={() => onEdit('overall')} testId="edit-overall" />}
          </SubHeading>
          {narrative.overallAssessment.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </Panel>
      )}
    </SectionShell>
  );
}
